import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Checkbox } from "@/components/ui/checkbox"
import { useUnits } from "@/hooks/useUnits"
import { useProfiles } from "@/hooks/useProfiles"
import { useCreateFinancialCharge, useCreateFinancialChargesBatch } from "@/hooks/useFinancialCharges"

const chargeSchema = z.object({
  unit_id: z.string().min(1, "Selecione uma unidade"),
  resident_id: z.string().optional(),
  description: z.string().min(1, "Descrição é obrigatória"),
  amount_cents: z.number().min(1, "Valor deve ser maior que zero"),
  due_date: z.string().min(1, "Data de vencimento é obrigatória"),
  payment_method: z.string().optional(),
  external_payment_link: z.string().url("Link deve ser uma URL válida").optional().or(z.literal("")),
  notes: z.string().optional(),
  is_automatic: z.boolean().optional(),
  generate_until: z.string().optional()
}).refine((data) => {
  if (data.is_automatic && !data.generate_until) {
    return false
  }
  return true
}, {
  message: "Data final é obrigatória para cobranças automáticas",
  path: ["generate_until"]
})

type ChargeFormData = z.infer<typeof chargeSchema>

interface CriarCobrancaModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const CriarCobrancaModal = ({ open, onOpenChange }: CriarCobrancaModalProps) => {
  const { data: units = [] } = useUnits()
  const { data: profiles = [] } = useProfiles()
  const createCharge = useCreateFinancialCharge()
  const createChargesBatch = useCreateFinancialChargesBatch()
  const [selectedUnitId, setSelectedUnitId] = useState("")

  const form = useForm<ChargeFormData>({
    resolver: zodResolver(chargeSchema),
    defaultValues: {
      unit_id: "",
      resident_id: "",
      description: "",
      amount_cents: 0,
      due_date: "",
      payment_method: "",
      external_payment_link: "",
      notes: "",
      is_automatic: false,
      generate_until: ""
    }
  })

  const isAutomatic = form.watch("is_automatic")

  const unitResidents = profiles.filter(profile => 
    profile.role === 'morador' && 
    units.find(unit => unit.id === selectedUnitId && unit.owner_id === profile.id)
  )

  const onSubmit = async (data: ChargeFormData) => {
    try {
      if (data.is_automatic && data.generate_until) {
        await createChargesBatch.mutateAsync({
          unit_id: data.unit_id,
          resident_id: data.resident_id || undefined,
          description: data.description,
          amount_cents: data.amount_cents,
          start_date: data.due_date,
          end_date: data.generate_until,
          payment_method: data.payment_method || undefined,
          external_payment_link: data.external_payment_link || undefined,
          notes: data.notes || undefined
        })
      } else {
        await createCharge.mutateAsync({
          unit_id: data.unit_id,
          resident_id: data.resident_id || undefined,
          description: data.description,
          amount_cents: data.amount_cents,
          due_date: data.due_date,
          payment_method: data.payment_method || undefined,
          external_payment_link: data.external_payment_link || undefined,
          notes: data.notes || undefined,
          status: 'pending',
          currency: 'BRL'
        })
      }
      onOpenChange(false)
      form.reset()
      setSelectedUnitId("")
    } catch (error) {
      console.error("Erro ao criar cobrança:", error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Nova Cobrança</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="unit_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unidade</FormLabel>
                  <Select 
                    onValueChange={(value) => {
                      field.onChange(value)
                      setSelectedUnitId(value)
                    }} 
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma unidade" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {units.map((unit) => (
                        <SelectItem key={unit.id} value={unit.id}>
                          {unit.building && `${unit.building} - `}Unidade {unit.number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedUnitId && unitResidents.length > 0 && (
              <FormField
                control={form.control}
                name="resident_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Morador (opcional)</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um morador" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {unitResidents.map((resident) => (
                          <SelectItem key={resident.id} value={resident.id}>
                            {resident.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Taxa de condomínio - Janeiro 2024" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amount_cents"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Valor (R$)</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      onChange={(e) => field.onChange(Math.round(parseFloat(e.target.value || "0") * 100))}
                      value={field.value ? (field.value / 100).toFixed(2) : ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="due_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data de Vencimento</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="payment_method"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Método de Pagamento (opcional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um método" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="boleto">Boleto</SelectItem>
                      <SelectItem value="pix">PIX</SelectItem>
                      <SelectItem value="card">Cartão</SelectItem>
                      <SelectItem value="external">Link Externo</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="external_payment_link"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Link de Pagamento (opcional)</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="https://..." 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações (opcional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Informações adicionais sobre a cobrança"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_automatic"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Cobrança automática (mensal)
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />

            {isAutomatic && (
              <FormField
                control={form.control}
                name="generate_until"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gerar até</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={createCharge.isPending || createChargesBatch.isPending}>
                {(createCharge.isPending || createChargesBatch.isPending) ? "Criando..." : "Criar Cobrança"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}